import React, { useState, useEffect, useRef } from "react";
import {
  FiCheckCircle,
  FiClock,
  FiShield,
  FiZap,
  FiUsers,
  FiFileText,
  FiCalendar,
  FiUserPlus,
  FiTrendingUp,
  FiAward,
  FiBookOpen,
  FiClipboard
} from "react-icons/fi";
import { useNavigate } from 'react-router-dom';

const OnboardingHeroSection = () => {
  const [statsVisible, setStatsVisible] = useState(false);
  const statsContainerRef = useRef(null);
  const navigate = useNavigate();
  const handleRecruit = () => navigate('/access-recruit');

  useEffect(() => {
    const timer = setTimeout(() => setStatsVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Duplicate stats for seamless infinite loop
  const statsData = [
    { value: "3 Days", label: "Avg Onboarding", icon: <FiClock /> },
    { value: "5K+", label: "New Hires", icon: <FiUserPlus /> },
    { value: "98%", label: "Completion Rate", icon: <FiCheckCircle /> },
    { value: "4.8/5", label: "Satisfaction", icon: <FiAward /> }
  ];

  // Duplicate the stats for seamless infinite scrolling
  const stats = [...statsData, ...statsData, ...statsData];

  const benefits = [
    { icon: <FiFileText />, text: "Digital Documentation", color: "#c62828" },
    { icon: <FiCalendar />, text: "Automated Scheduling", color: "#2563eb" },
    { icon: <FiBookOpen />, text: "Training Modules", color: "#ffa726" },
    { icon: <FiClipboard />, text: "Task Checklists", color: "#00897b" },
    { icon: <FiUsers />, text: "Team Integration", color: "#9c27b0" },
    { icon: <FiShield />, text: "Compliance Tracking", color: "#f44336" },
  ];

  return (
    <section className="bst-hero-section">
      {/* Animated Background Elements */}
      <div className="bst-grid-pattern"></div>

      <div className="bst-hero-container">
        {/* Main Content - Left */}
        <div className="bst-hero-content">
          {/* Main Title */}
          <h1 className="bst-hero-title">
            <span className="bst-title-primary">Seamless<span className="bst-title-secondary">Employee Onboarding</span> From Day Zero to Hero</span>
          </h1>

          {/* Subtitle */}
          <p className="bst-hero-subtitle">
            Transform new hire experiences with automated workflows, personalized learning paths, and seamless integration. 
            Get your team productive faster with our intelligent onboarding platform.
          </p>

          {/* Call-to-Action Buttons */}
          <div className="bst-cta-buttons">
            <button className="bst-btn bst-btn-primary" onClick={handleRecruit}>
              <span>Start Onboarding</span>
              <FiZap className="bst-btn-icon" />
            </button>
            <button className="bst-btn bst-btn-secondary" onClick={handleRecruit}>
              <span>Watch Demo</span>
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="bst-trust-row">
            <div className="bst-trust-item">
              <FiShield className="bst-trust-icon" />
              <span>GDPR Compliant</span>
            </div>
            <div className="bst-trust-item">
              <FiClock className="bst-trust-icon" />
              <span>24-Hour Setup</span>
            </div>
            <div className="bst-trust-item">
              <FiCheckCircle className="bst-trust-icon" />
              <span>Free Trial</span>
            </div>
          </div>
        </div>

        {/* Hero Image - Right */}
        <div className="bst-hero-image-wrapper">
          <div className="bst-image-glow"></div>
          <div className="bst-image-container">
            <img
              src="/images/Employee_onboarding.png"
              alt="Employee Onboarding Dashboard"
              className="bst-hero-image"
            />

            {/* Floating Stats Cards */}
            <div className="bst-float-card bst-card-1">
              <div className="bst-card-icon bst-icon-primary">
                <FiUserPlus />
              </div>
              <div className="bst-card-content">
                <div className="bst-card-value">156</div>
                <div className="bst-card-label">This Month</div>
              </div>
            </div>

            <div className="bst-float-card bst-card-2">
              <div className="bst-card-icon bst-icon-success">
                <FiTrendingUp />
              </div>
              <div className="bst-card-content">
                <div className="bst-card-value">87%</div>
                <div className="bst-card-label">Faster Process</div>
              </div>
            </div>

            <div className="bst-float-card bst-card-3">
              <div className="bst-card-icon bst-icon-warning">
                <FiCheckCircle />
              </div>
              <div className="bst-card-content">
                <div className="bst-card-value">98%</div>
                <div className="bst-card-label">Complete</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* STATS SECTION - Continuous Slider */}
      <div className="bst-stats-wrapper">
        <div className="bst-stats-slider-container">
          <div 
            className={`bst-stats-slider-track ${statsVisible ? 'bst-stats-slider-running' : ''}`}
            ref={statsContainerRef}
          >
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bst-stat-card-slider"
                style={{ 
                  // Calculate position for seamless loop
                  '--item-index': index % statsData.length
                }}
              >
                <div className="bst-stat-icon">{stat.icon}</div>
                <div className="bst-stat-value">{stat.value}</div>
                <div className="bst-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
          
          {/* Gradient overlays for fade effect */}
          <div className="bst-slider-fade bst-slider-fade-left"></div>
          <div className="bst-slider-fade bst-slider-fade-right"></div>
        </div>
      </div>

      {/* Benefits Grid - Separate Section */}
      <div className="bst-benefits-section">
        <h3 className="bst-benefits-title">Complete <span className="bst-title-secondary">Onboarding Solution</span></h3>
        <div className="bst-benefits-grid">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="bst-benefit-card"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="bst-benefit-icon" style={{ color: benefit.color }}>
                {benefit.icon}
              </div>
              <span className="bst-benefit-text">{benefit.text}</span>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        /* Import Google Fonts */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Poppins:wght@400;600;700;800&display=swap');

        /* ===========================
           MAIN HERO SECTION - ENLARGED
        =========================== */
        .bst-hero-section {
          position: relative;
          width: 100%;
          height: auto;
          background: linear-gradient(135deg, #ffffff 0%, #ffffff 100%);
          overflow: hidden;
          padding: 100px 20px 80px;
          font-family: 'Inter', sans-serif;
          max-width: 100vw;
          overflow-x: hidden;
        }

        /* ===========================
           BACKGROUND ANIMATIONS - ENLARGED
        =========================== */
        .bst-bg-gradient-overlay {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 50% 50%, rgba(198, 40, 40, 0.03) 0%, transparent 70%);
          pointer-events: none;
        }

        .bst-grid-pattern {
          position: absolute;
          inset: 0;
          background-image: 
            linear-gradient(rgba(198, 40, 40, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(198, 40, 40, 0.02) 1px, transparent 1px);
          background-size: 80px 80px;
          opacity: 0.6;
        }

        /* ===========================
           CONTAINER - ENLARGED
        =========================== */
        .bst-hero-container {
          position: relative;
          max-width: 1440px;
          margin: 0 auto;
          z-index: 1;
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          align-items: center;
          gap: 80px;
          margin-top: 100px;
          padding: 0 30px;
          width: 100%;
        }

        /* ===========================
           HERO CONTENT (Left) - ENLARGED
        =========================== */
        .bst-hero-content {
          text-align: left;
          max-width: 650px;
          animation: bstFadeInUp 1s ease-out;
        }

        @keyframes bstFadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Main Title - ENLARGED */
        .bst-hero-title {
          font-size: 60px;
          line-height: 1.2;
          margin-bottom: 30px;
          font-weight: 900;
          font-family: "Raleway", sans-serif;
          display: flex;
          flex-direction: column;
          gap: 12px;
          animation: bstTitleReveal 1.2s ease-out;
        }

        @keyframes bstTitleReveal {
          from {
            opacity: 0;
            transform: translateY(30px);
            filter: blur(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
            filter: blur(0);
          }
        }

        .bst-title-primary {
          color: #232b35;
          font-weight: 900;
        }

        .bst-title-secondary {
          color: #c62828;
          font-weight: 900;
        }

        .bst-title-accent {
          color: #1e2330;
          font-weight: 900;
          font-size: 58px;
          font-family: "Raleway", sans-serif;
        }

        /* Subtitle - ENLARGED */
        .bst-hero-subtitle {
          font-size: 20px;
          line-height: 1.8;
          color: #555;
          margin-bottom: 40px;
          max-width: 700px;
          animation: bstFadeIn 1.5s ease-out;
        }

        @keyframes bstFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        /* ===========================
           CTA BUTTONS - ENLARGED
        =========================== */
        .bst-cta-buttons {
          display: flex;
          gap: 25px;
          margin-bottom: 40px;
          flex-wrap: wrap;
        }

        .bst-btn {
          padding: 22px 48px;
          border: none;
          border-radius: 50px;
          font-size: 17px;
          font-weight: 700;
          letter-spacing: 0.5px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 12px;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
          font-family: 'Inter', sans-serif;
        }

        .bst-btn::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.3);
          transform: translate(-50%, -50%);
          transition: width 0.6s ease, height 0.6s ease;
        }

        .bst-btn:hover::before {
          width: 400px;
          height: 400px;
        }

        .bst-btn-primary {
          background: linear-gradient(135deg, #c62828);
          color: white;
          box-shadow: 0 8px 25px rgba(198, 40, 40, 0.4);
        }

        .bst-btn-primary:hover {
          transform: translateY(-8px);
          box-shadow: 0 15px 40px rgba(198, 40, 40, 0.5);
        }

        .bst-btn-secondary {
          background: linear-gradient(135deg, #232b35, #3d4956);
          color: white;
          box-shadow: 0 8px 25px rgba(35, 43, 53, 0.4);
        }

        .bst-btn-secondary:hover {
          transform: translateY(-8px);
          box-shadow: 0 15px 40px rgba(35, 43, 53, 0.5);
        }

        .bst-btn-icon {
          font-size: 20px;
          position: relative;
          z-index: 1;
        }

        .bst-btn span {
          position: relative;
          z-index: 1;
        }

        /* ===========================
           TRUST INDICATORS - ENLARGED
        =========================== */
        .bst-trust-row {
          display: flex;
          gap: 40px;
          flex-wrap: wrap;
          animation: bstSlideUp 1.8s ease-out;
        }

        @keyframes bstSlideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .bst-trust-item {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 16px;
          color: #666;
          font-weight: 600;
        }

        .bst-trust-icon {
          color: #00897b;
          font-size: 22px;
        }

        /* ===========================
           HERO IMAGE (Right) - ENLARGED
        =========================== */
        .bst-hero-image-wrapper {
          position: relative;
          width: 100%;
          min-width: 650px;
          min-height: 450px;
          animation: bstZoomIn 1.3s ease-out;
        }

        @keyframes bstZoomIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .bst-image-glow {
          position: absolute;
          inset: 20px;
          background: radial-gradient(circle, rgba(255, 3, 3, 0.61), transparent 70%);
          filter: blur(80px);
          z-index: -1;
          animation: bstGlowPulse 4s ease-in-out infinite;
        }

        @keyframes bstGlowPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }

        .bst-image-container {
          position: relative;
          width: 100%;
          max-width: 750px;
          min-height: 450px;
          border-radius: 24px;
          overflow: visible;
          margin: 0 auto;
          height: auto;
          box-shadow: 0 25px 80px rgba(0, 0, 0, 0.2);
        }

        .bst-hero-image {
          width: 80%;
          position:absolute;
          left:10%;
          height: auto;
          display: block;
          border-radius: 24px;
        }

        /* ===========================
           FLOATING CARDS - ENLARGED
        =========================== */
        .bst-float-card {
          position: absolute;
          background: white;
          padding: 24px;
          border-radius: 18px;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
          display: flex;
          align-items: center;
          gap: 18px;
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.8);
          animation: bstCardFloat 5s ease-in-out infinite;
        }

        .bst-card-1 {
          top: -15px;
          left: -60px;
          animation-delay: 0s;
        }

        .bst-card-2 {
          top: 20%;
          right: -25px;
          transform: translateY(-50%);
          animation-delay: -2s;
        }

        .bst-card-3 {
          bottom: -15px;
          left: -45px;
          transform: translateY(-50%);
          animation-delay: -4s;
        }

        @keyframes bstCardFloat {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg); 
          }
          50% { 
            transform: translateY(-20px) rotate(2deg);
          }
        }

        .bst-card-icon {
          width: 65px;
          height: 65px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 28px;
          flex-shrink: 0;
        }

        .bst-icon-primary {
          background: linear-gradient(135deg, #c62828, #ff5252);
        }

        .bst-icon-success {
          background: linear-gradient(135deg, #00897b, #26a69a);
        }

        .bst-icon-warning {
          background: linear-gradient(135deg, #ffa726, #ffb74d);
        }

        .bst-card-content {
          display: flex;
          flex-direction: column;
        }

        .bst-card-value {
          font-size: 32px;
          font-weight: 900;
          color: #232b35;
          line-height: 1;
          margin-bottom: 6px;
        }

        .bst-card-label {
          font-size: 15px;
          color: #666;
          font-weight: 600;
        }

        /* ===========================
           STATS SLIDER SECTION - CONTINUOUS RUNNING
           (Updated: No box shadow, Proper card size)
        =========================== */
        .bst-stats-wrapper {
          display: flex;
          justify-content: center;
          margin: 60px 0 40px;
          width: 100%;
          padding: 0 20px;
          overflow: hidden;
          position: relative;
          background: transparent;
        }
        
        .bst-stats-slider-container {
          position: relative;
          width: 100%;
          max-width: 1440px;
          margin: 0 auto;
          overflow: hidden;
          background: transparent;
          padding: 30px 0;
          box-shadow: none; /* Removed box shadow */
        }
        
        .bst-stats-slider-track {
          display: flex;
          gap: 30px; /* Reduced gap */
          width: max-content;
          animation: bstSliderScroll 40s linear infinite;
          animation-play-state: paused;
          padding: 0 20px;
        }
        
        .bst-stats-slider-running {
          animation-play-state: running;
        }
        
        @keyframes bstSliderScroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(calc(-33.33% - 10px)); /* Adjusted for reduced gap */
          }
        }
        
        .bst-stat-card-slider {
          flex: 0 0 auto;
          width: 290px; /* Reduced width for proper size */
          height: 200px; /* Fixed height */
          background: white;
          border-radius: 16px;
          padding: 25px 20px; /* Reduced padding */
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05); /* Minimal shadow for depth */
        }
        
        .bst-stat-card-slider:hover {
          transform: translateY(-5px);
          border-color: rgba(198, 40, 40, 0.2);
          box-shadow: 0 8px 20px rgba(198, 40, 40, 0.1);
        }
        
        .bst-stat-icon {
          font-size: 32px; /* Reduced icon size */
          color: #c62828;
          margin-bottom: 12px;
          animation: bstIconBounce 2s ease-in-out infinite;
        }

        @keyframes bstIconBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        
        .bst-stat-value {
          font-size: 32px; /* Reduced font size */
          font-weight: 800;
          background: linear-gradient(135deg, #c62828, #ff5252);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 8px;
          font-family: 'Poppins', sans-serif;
          text-align: center;
          line-height: 1.2;
        }
        
        .bst-stat-label {
          font-size: 14px; /* Reduced font size */
          color: #666;
          font-weight: 600;
          text-align: center;
          line-height: 1.3;
        }
        
        /* Gradient fade effects on edges */
        .bst-slider-fade {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 100px;
          pointer-events: none;
          z-index: 2;
        }
        
        .bst-slider-fade-left {
          left: 0;
          background: linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
        }
        
        .bst-slider-fade-right {
          right: 0;
          background: linear-gradient(270deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
        }

        /* ===========================
           BENEFITS SECTION - ENLARGED
        =========================== */
        .bst-benefits-section {
          width: 100%;
          position: relative;
          max-width: 1440px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .bst-benefits-title {
          font-size: 2.8rem;
          font-weight: 900;
          text-align: center;
          color: #232b35;
          margin-bottom: 50px;
          font-family: 'Poppins', sans-serif;
        }

        .bst-benefits-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
          width: 100%;
        }

        .bst-benefit-card {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 28px 35px;
          background: white;
          border-radius: 20px;
          border: 1px solid rgba(0, 0, 0, 0.06);
          opacity: 0;
          transform: translateX(-30px);
          animation: bstBenefitSlide 0.8s ease-out forwards;
          transition: all 0.3s ease;
        }

        @keyframes bstBenefitSlide {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .bst-benefit-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 18px 40px rgba(0, 0, 0, 0.12);
          border-color: rgba(198, 40, 40, 0.2);
        }

        .bst-benefit-icon {
          font-size: 36px;
          margin-left: 70px;
          flex-shrink: 0;
          animation: bstIconPulse 2.5s ease-in-out infinite;
        }

        @keyframes bstIconPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }

        .bst-benefit-text {
          font-size: 18px;
          text-align: center;
          font-weight: 600;
          color: #333;
        }

        /* ===========================
           RESPONSIVE DESIGN FOR SLIDER
        =========================== */
        @media screen and (max-width: 1400px) {
          .bst-hero-title {
            font-size: 56px;
          }

          .bst-title-accent {
            font-size: 52px;
          }

          .bst-hero-container {
            gap: 60px;
            max-width: 1200px;
          }
          
          .bst-image-container {
            max-width: 650px;
          }
          
          .bst-benefits-title {
            font-size: 46px;
          }
          
          .bst-stat-card-slider {
            width: 200px;
            height: 150px;
          }
          
          .bst-stats-slider-container {
            max-width: 1200px;
          }
        }

        @media screen and (max-width: 1200px) {
          .bst-hero-title {
            font-size: 48px;
          }

          .bst-title-accent {
            font-size: 44px;
          }

          .bst-float-card {
            display: none;
          }
          
          .bst-hero-container {
            grid-template-columns: 1fr;
            gap: 50px;
            margin-top: 60px;
          }

          .bst-hero-content {
            text-align: center;
            max-width: 100%;
            margin: 0 auto;
          }

          .bst-hero-subtitle {
            font-size: 18px;
            margin: 0 auto 40px;
          }

          .bst-hero-image-wrapper {
            min-width: auto;
            min-height: auto;
            order: 2;
          }

          .bst-cta-buttons {
            justify-content: center;
          }

          .bst-trust-row {
            justify-content: center;
          }

          .bst-benefits-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .bst-benefits-title {
            font-size: 42px;
          }
          
          .bst-stat-card-slider {
            width: 180px;
            height: 140px;
            padding: 20px 15px;
          }
          
          .bst-stats-slider-track {
            gap: 25px;
          }
          
          .bst-stat-value {
            font-size: 28px;
          }
          
          .bst-stat-icon {
            font-size: 28px;
          }
        }

        @media screen and (max-width: 992px) {
          .bst-hero-section {
            padding: 80px 20px 60px;
          }
          
          .bst-hero-title {
            font-size: 42px;
          }

          .bst-title-accent {
            font-size: 38px;
          }

          .bst-benefits-title {
            font-size: 38px;
          }

          .bst-benefits-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .bst-trust-row {
            flex-direction: column;
            gap: 25px;
            align-items: center;
          }
          
          .bst-stats-slider-container {
            padding: 25px 0;
          }
          
          .bst-stat-card-slider {
            width: 160px;
            height: 130px;
            padding: 18px 12px;
          }
          
          .bst-stat-value {
            font-size: 26px;
          }
          
          .bst-stat-icon {
            font-size: 26px;
            margin-bottom: 10px;
          }
          
          .bst-stat-label {
            font-size: 13px;
          }
        }

        @media screen and (max-width: 768px) {
          .bst-hero-title {
            font-size: 36px;
          }

          .bst-title-accent {
            font-size: 32px;
          }

          .bst-benefits-title {
            font-size: 34px;
          }

          .bst-benefits-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .bst-hero-subtitle {
            font-size: 16px;
          }
          
          .bst-stat-card-slider {
            width: 150px;
            height: 120px;
            padding: 15px 10px;
          }
          
          .bst-stat-value {
            font-size: 24px;
          }
          
          .bst-stat-icon {
            font-size: 24px;
            margin-bottom: 8px;
          }
          
          .bst-stat-label {
            font-size: 12px;
          }
          
          .bst-benefit-card {
            padding: 24px 28px;
          }
          
          .bst-benefit-icon {
            font-size: 32px;
          }
          
          .bst-benefit-text {
            font-size: 16px;
          }
          
          /* Adjust animation speed for mobile */
          @keyframes bstSliderScroll {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(calc(-50% - 12px));
            }
          }
          
          .bst-slider-fade {
            width: 60px;
          }
        }

        @media screen and (max-width: 576px) {
          .bst-hero-title {
            font-size: 32px;
          }

          .bst-title-accent {
            font-size: 28px;
          }

          .bst-hero-subtitle {
            font-size: 15px;
          }

          .bst-cta-buttons {
            flex-direction: column;
            width: 100%;
            max-width: 400px;
            margin: 0 auto 35px;
            gap: 15px;
          }

          .bst-btn {
            width: 100%;
            justify-content: center;
            padding: 20px 32px;
            font-size: 16px;
          }

          .bst-benefits-title {
            font-size: 30px;
          }
          
          .bst-image-container {
            max-width: 100%;
            min-height: 300px;
          }
          
          .bst-stats-wrapper {
            padding: 0 15px;
            margin: 40px 0 30px;
          }
          
          .bst-stat-card-slider {
            width: 140px;
            height: 110px;
            padding: 12px 8px;
          }
          
          .bst-stat-value {
            font-size: 22px;
          }
          
          .bst-stat-icon {
            font-size: 22px;
            margin-bottom: 6px;
          }
          
          .bst-stat-label {
            font-size: 11px;
          }
          
          .bst-stats-slider-track {
            gap: 20px;
          }
          
          .bst-slider-fade {
            width: 40px;
          }
        }

        /* Pause animation on hover for better UX */
        .bst-stats-slider-track:hover {
          animation-play-state: paused;
        }

        /* Additional fix for very wide screens */
        @media screen and (min-width: 1600px) {
          .bst-hero-container {
            max-width: 1500px;
          }
          
          .bst-stats-slider-container,
          .bst-benefits-section {
            max-width: 1300px;
          }
          
          .bst-stat-card-slider {
            width: 240px;
            height: 170px;
          }
        }
        
        @media screen and (min-width: 2000px) {
          .bst-hero-container {
            max-width: 1600px;
          }
          
          .bst-hero-title {
            font-size: 72px;
          }
          
          .bst-title-accent {
            font-size: 64px;
          }
          
          .bst-hero-subtitle {
            font-size: 24px;
          }
          
          .bst-stat-card-slider {
            width: 260px;
            height: 180px;
            padding: 30px 25px;
          }
          
          .bst-stat-value {
            font-size: 36px;
          }
          
          .bst-stat-icon {
            font-size: 36px;
          }
        }
      `}</style>
    </section>
  );
};

export default OnboardingHeroSection;